import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import {
  DocumentVaultItemModel,
  DocumentVaultUploadData,
  DocumentVaultDeleteData,
  DocumentVaultSearchData,
  DocumentVaultUpdateProfileLabelsData,
} from '../../models/document.vault.item.model';
import { revertAll, RootState } from '../../store';
import { AIDocumentContractDeleteData } from '../../models/ai.document.contract.model';

export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async ({ data }: DocumentVaultSearchData) => {
    const response: any = await NWClient.list('vault-documents', data);
    return { results: response.results as DocumentVaultItemModel[], count: response.count };
  }
);

export const uploadDocument = createAsyncThunk(
  'documents/uploadDocument',
  async ({ data }: DocumentVaultUploadData) => {
    const response = await NWClient.post('document-upload/bulk_upload', data, true);
    return response;
  }
);

export const updateDocumentProfileItems = createAsyncThunk(
  'documents/updateDocumentProfileItems',
  async ({ id, data }: DocumentVaultUpdateProfileLabelsData) => {
    const response = await NWClient.patch(`document-upload/${id}/`, null, data);
    return response;
  }
);

export const deleteDocument = createAsyncThunk(
  'documents/deleteDocument',
  async ({ id, userId }: DocumentVaultDeleteData) => {
    const response = await NWClient.delete('document-upload', id, null, { user: userId });
    return response;
  }
);

export const deleteDocumentContract = createAsyncThunk(
  'documentContracts/deleteDocumentContract',
  async ({ id, userId }: AIDocumentContractDeleteData) => {
    const response = await NWClient.delete('document-contract', id, null, { user: userId });
    return response;
  }
);

export interface DocumentsState {
  documentsList: DocumentVaultItemModel[];
  documentsCount: number;
  status: string;
  error: string;
}

const documentsArray: DocumentVaultItemModel[] = [];
const initialState: DocumentsState = {
  documentsList: documentsArray,
  documentsCount: 0,
  status: 'idle',
  error: '',
};
const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearDocuments(state) {
      state.documentsList = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (Array.isArray(action.payload.results)) {
          state.documentsList = [...action.payload.results];
          state.documentsCount = action.payload.count || 0;
        }
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(uploadDocument.pending, (state) => {
        state.status = 'uploading';
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.status = 'uploaded';
        state.documentsList.push(action.payload as DocumentVaultItemModel);
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateDocumentProfileItems.pending, (state) => {
        state.status = 'updating';
      })
      .addCase(updateDocumentProfileItems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.documentsList.findIndex((doc) => doc.id === action.meta.arg.id);
        state.documentsList[index] = { ...(action.payload as DocumentVaultItemModel) };
      })
      .addCase(updateDocumentProfileItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteDocument.pending, (state) => {
        state.status = 'deleting';
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.documentsList = [...state.documentsList].filter((el) => el.id !== action.meta.arg.id);
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteDocumentContract.pending, (state) => {
        state.status = 'deleteLoading';
      })
      .addCase(deleteDocumentContract.fulfilled, (state, action) => {
        state.status = 'deleteSucceeded';
        state.documentsList = [...state.documentsList].filter((el) => el.id !== action.meta.arg.id);
      })
      .addCase(deleteDocumentContract.rejected, (state, action) => {
        state.status = 'deleteFailed';
        state.error = action.error.message;
      })
      .addCase(revertAll, () => initialState);
  },
});

export default documentsSlice.reducer;
export const { clearDocuments } = documentsSlice.actions;
export const selectAllDocuments = (state: RootState) => state.documents.documentsList;
export const selectDocumentsCount = (state: RootState) => state.documents.documentsCount;
